import { useRouteParam } from "@/shared/routing/useRouteParam.ts";
import { parseIsoDate } from "@/shared/utils/Dates.ts";
import type { LaborCostSection } from "@/pages/human-resources/time-management/business/analysis/LaborCostSection.ts";
import { useReadLaborCostDetails } from "@/pages/human-resources/time-management/services/hooks/useReadLaborCostDetails.ts";
import useScheduleReferenceData from "@/pages/human-resources/time-management/schedules/reference-provider/useScheduleReferenceData.ts";
import { LEGACY_TIME_ZONE } from "@/shared/utils/LegacyDates.ts";
import useScheduleRangeOnRoute from "@/pages/human-resources/time-management/hooks/useScheduleRangeOnRoute.ts";
import useScheduleUserPermissions from "@/pages/human-resources/time-management/hooks/useScheduleUserPermissions.ts";
import {
  computeEndDateForRange,
  type ScheduleRange,
} from "@/pages/human-resources/time-management/model/ScheduleRange.ts";

export interface UseLaborCostDetailsCardController {
  isEnabled: boolean;
  isFetching: boolean;
  range: ScheduleRange;
  startDate: Date;
  endDate: Date;
  sections: LaborCostSection[];
}

export function useLaborCostDetailsCardController(): UseLaborCostDetailsCardController {
  const {
    domainData: { domainId },
    siteData: { siteId },
  } = useScheduleReferenceData();

  const { canSeeSalaries } = useScheduleUserPermissions();

  const dateStr = useRouteParam("date");
  const range = useScheduleRangeOnRoute();
  const startDate = parseIsoDate(dateStr, LEGACY_TIME_ZONE);
  const endDate = computeEndDateForRange(startDate, range);

  const result = useReadLaborCostDetails(
    {
      domainId: domainId,
      siteId: siteId,
      date: startDate.toISOString(),
      range,
    },
    canSeeSalaries,
  );

  return {
    isEnabled: canSeeSalaries,
    isFetching: result.isFetching,
    range,
    startDate,
    endDate,
    sections: result.data ?? [],
  };
}
